// Formateo compartido para la vista de soporte.
//
// Todo en es-AR y zona horaria local del operador: quien atiende la llamada
// razona en la hora que ve en su reloj, no en UTC.

const RELATIVE = new Intl.RelativeTimeFormat('es-AR', { numeric: 'auto' });

const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 31_536_000],
  ['month', 2_592_000],
  ['day', 86_400],
  ['hour', 3_600],
  ['minute', 60],
];

/**
 * "hace 3 días" / "en 2 meses". La fecha exacta va SIEMPRE en el `title` del
 * elemento que use esto: lo relativo se lee de un vistazo, pero para reclamar
 * un cobro hace falta el timestamp preciso.
 */
export function relativeTime(input: string | number | Date | null | undefined): string {
  if (input == null) return '—';
  const date = typeof input === 'number' ? new Date(input * 1000) : new Date(input);
  if (Number.isNaN(date.getTime())) return '—';

  return relativeSeconds((date.getTime() - Date.now()) / 1000);
}

/**
 * Igual que `relativeTime` pero desde un delta ya medido: "hace 3 horas" a
 * partir de -10800. Existe para las diferencias que mide el SERVIDOR — ahí el
 * reloj del navegador no participa del cálculo y no puede corrernos el
 * resultado (ver la ventana de gracia en la vista 360).
 */
export function relativeSeconds(diffSeconds: number): string {
  const abs = Math.abs(diffSeconds);
  if (abs < 45) return 'recién';

  for (const [unit, seconds] of UNITS) {
    if (abs >= seconds) {
      return RELATIVE.format(Math.round(diffSeconds / seconds), unit);
    }
  }
  return RELATIVE.format(Math.round(diffSeconds / 60), 'minute');
}

/**
 * Cuenta regresiva legible de un plazo en segundos: `04:59:58`, `2 d 03:12:44`.
 * Hora, minuto y segundo van con dos dígitos siempre para que el número no
 * "salte" de ancho al pasar de 10:00:00 a 9:59:59.
 */
export function countdown(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const pad = (n: number): string => String(n).padStart(2, '0');
  const clock = `${pad(Math.floor((s % 86_400) / 3_600))}:${pad(Math.floor((s % 3_600) / 60))}:${pad(s % 60)}`;
  const days = Math.floor(s / 86_400);
  return days > 0 ? `${days} d ${clock}` : clock;
}

/** Fecha + hora completas, para tooltips y campos donde importa el minuto. */
export function absoluteTime(input: string | number | Date | null | undefined): string {
  if (input == null) return '—';
  const date = typeof input === 'number' ? new Date(input * 1000) : new Date(input);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('es-AR', { dateStyle: 'medium', timeStyle: 'short' });
}

/** Solo fecha, para vencimientos y periodos. */
export function shortDate(input: string | number | Date | null | undefined): string {
  if (input == null) return '—';
  const date = typeof input === 'number' ? new Date(input * 1000) : new Date(input);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function money(amount: number | string | null | undefined, currency = 'ARS'): string {
  if (amount == null) return '—';
  const value = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency }).format(value);
}

/**
 * Días que faltan (o que pasaron) hasta una fecha. Negativo = ya vencio.
 * Null si no hay fecha.
 */
export function daysUntil(input: string | null | undefined): number | null {
  if (!input) return null;
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return null;
  return Math.ceil((date.getTime() - Date.now()) / 86_400_000);
}
